import React, { useEffect, useState } from 'react';
import P, { InferProps } from 'prop-types';
import * as yup from 'yup';
import { SxStyle } from '../../../types';
import Grid from '@mui/material/Grid';
import SwitchField from './switch-field';
import DateTimeField from './datetime-field';
import { useStringsContext } from '../../../contexts/strings';
import FormHelperText from '@mui/material/FormHelperText';

const TIMED_START_FIELD_KEY = 'promosTimedStart';
const TIMED_END_FIELD_KEY = 'promosTimedEnd';

const styles: SxStyle = {
  errorMessage: {
    paddingLeft: '10px',
  },
};

function PromoFieldTimed({
  setChange,
  setValidation,
  field,
  isReadOnly,
  path,
}: InferProps<typeof PromoFieldTimed.propTypes>) {
  const { strings } = useStringsContext();
  const [showDateField, setShowDateField] = useState<boolean>(false);
  const [timedStartDate, setTimedStartDate] = useState<string>('');
  const [timedEndDate, setTimedEndDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (field.value && Array.isArray(field.value)) {
      if (field.value[0] !== timedStartDate) {
        setTimedStartDate(field.value[0]);
      }
      if (field.value[1] !== timedEndDate) {
        setTimedEndDate(field.value[1]);
      }
      setShowDateField(true);
    }
  }, []);

  useEffect(() => {
    if (timedStartDate && timedEndDate) {
      // Validate that the start date is before the end date
      const yupSchema = yup
        .date()
        .min(new Date(timedStartDate), strings.validations.validDateStartBeforeEnd);
      let isValid = false;

      try {
        yupSchema.validateSync(timedEndDate);
        setErrorMessage('');
        isValid = true;
      } catch (error: any) {
        setErrorMessage(error.message);
      }
      setValidation(field.key, isValid, yup.boolean().isTrue());
    }
  }, [field.key, setValidation, strings, timedEndDate, timedStartDate]);

  const onChangeToggle = (
    fieldKey: string,
    value: boolean,
    fieldValidation: any,
    isTeamDataValue: boolean
  ) => {
    setShowDateField(value);
    if (!value) {
      // Reset validation and values when toggle is off
      setValidation(field.key, true, yup.boolean().isTrue());
      setValidation(TIMED_START_FIELD_KEY, true, yup.boolean().isTrue());
      setValidation(TIMED_END_FIELD_KEY, true, yup.boolean().isTrue());
      setTimedStartDate('');
      setTimedEndDate('');
      setErrorMessage('');
      setChange(field.key, null, fieldValidation, isTeamDataValue);
    }
  };

  const onChangeTimedStartDate = (
    fieldKey: string,
    value: string,
    fieldValidation: any,
    isTeamDataValue: boolean
  ) => {
    setTimedStartDate(value);
    const timedStartEndValue = [value, timedEndDate];
    setChange(field.key, timedStartEndValue, fieldValidation, isTeamDataValue);
  };

  const onChangeTimedEndDate = (
    fieldKey: string,
    value: string,
    fieldValidation: any,
    isTeamDataValue: boolean
  ) => {
    setTimedEndDate(value);
    const timedStartEndValue = [timedStartDate, value];
    setChange(field.key, timedStartEndValue, fieldValidation, isTeamDataValue);
  };

  return (
    <Grid container spacing={4} alignItems="center">
      <Grid item>
        <SwitchField field={field} setChange={onChangeToggle} isReadOnly={isReadOnly} />
      </Grid>
      {showDateField && (
        <Grid item container spacing={2}>
          <Grid item>
            <DateTimeField
              field={{
                title: strings.fields.promoTimedStart,
                id: 'promos-timed-start',
                key: TIMED_START_FIELD_KEY,
                value: timedStartDate,
                validation: yup
                  .date()
                  .required(strings.validations.isRequired)
                  .typeError(strings.validations.validDateTime),
              }}
              setChange={onChangeTimedStartDate}
              setValidation={setValidation}
              isReadOnly={isReadOnly}
            />
          </Grid>
          <Grid item>
            <DateTimeField
              field={{
                title: strings.fields.promoTimedEnd,
                id: 'promos-timed-end',
                key: TIMED_END_FIELD_KEY,
                value: timedEndDate,
                validation: yup
                  .date()
                  .required(strings.validations.isRequired)
                  .typeError(strings.validations.validDateTime),
              }}
              setChange={onChangeTimedEndDate}
              setValidation={setValidation}
              isReadOnly={isReadOnly}
            />
          </Grid>
          <FormHelperText error sx={styles.errorMessage}>
            {errorMessage}
          </FormHelperText>
        </Grid>
      )}
    </Grid>
  );
}

PromoFieldTimed.propTypes = {
  setChange: P.func.isRequired,
  setValidation: P.func.isRequired,
  isReadOnly: P.bool,
  field: P.any.isRequired,
  key: P.string,
  path: P.string.isRequired,
};

export default PromoFieldTimed;
